import { useRegisterSW } from 'virtual:pwa-register/react'
import { RefreshCw, X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'

/** How often an open tab re-checks for a newer deploy while it sits idle. */
const UPDATE_CHECK_MS = 60 * 60 * 1000

/**
 * Tells the player a new build shipped; it never reloads on its own. A deploy must not yank
 * someone out of a live game, so the tab keeps its socket and turn state until they tap Refresh
 * (`registerType: 'prompt'` in vite.config.ts — the opposite of `'autoUpdate'`, which would
 * silently reload every open tab the moment the new build finishes installing).
 */
export function UpdateBanner() {
  const [dismissed, setDismissed] = useState(false)
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return
      setInterval(() => void registration.update(), UPDATE_CHECK_MS)
    },
  })

  return (
    <AnimatePresence>
      {needRefresh && !dismissed && (
        <motion.div
          role="status"
          initial={{ y: -48, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -48, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 420, damping: 32 }}
          className="fixed inset-x-0 top-0 z-40 flex justify-center px-3 pt-[env(safe-area-inset-top)]"
        >
          <div className="mt-2 flex items-center gap-1 rounded-full bg-slate-900 py-1.5 pl-4 pr-1.5 text-sm font-medium text-white shadow-lg dark:bg-white dark:text-slate-900">
            <span className="mr-1">A new version is ready</span>
            <button
              type="button"
              onClick={() => void updateServiceWorker(true)}
              className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold hover:bg-white/25 dark:bg-slate-900/10 dark:hover:bg-slate-900/20"
            >
              <RefreshCw className="size-3.5" aria-hidden /> Refresh
            </button>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              aria-label="Dismiss"
              className="inline-flex size-7 items-center justify-center rounded-full hover:bg-white/15 dark:hover:bg-slate-900/10"
            >
              <X className="size-3.5" aria-hidden />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
